import moment, { type Moment } from 'moment';
import { useCallback, useMemo } from 'react';

import { useNewUserAccessRights, useUserPrint } from '@app/hooks';

interface UseIndividualCalendarPermissionsProps {
  currentEmployeeId: number;
  todayMoment: Moment;
}

export const useIndividualCalendarPermissions = ({
  currentEmployeeId,
  todayMoment,
}: UseIndividualCalendarPermissionsProps) => {
  const {
    params: { UserId },
  } = useUserPrint();
  const {
    userAccessRights: {
      Clients: { IsShowContacts },
      Records: {
        IsOn: RecordsIsOn,
        IsCreate: RecordsIsCreate,
        IsEdit: RecordsIsEdit,
        IsJournal: RecordsIsJournal,
        IsViewAllRecords,
        IsViewPastRecords,
      },
      WorkSchedules: {
        IsOn: WorkSchedulesIsOn,
        IsCreate: WorkSchedulesIsCreate,
        IsEdit: WorkSchedulesIsEdit,
        IsEditPast: WorkSchedulesIsEditPast,
      },
    },
  } = useNewUserAccessRights();

  const currentUserId = useMemo(() => Number(UserId), [UserId]);

  const canAccessJournal = useMemo(
    () => RecordsIsOn && RecordsIsJournal,
    [RecordsIsJournal, RecordsIsOn]
  );

  const canViewCurrentEmployee = useMemo(
    () => IsViewAllRecords || currentEmployeeId === currentUserId,
    [IsViewAllRecords, currentEmployeeId, currentUserId]
  );

  const canViewPage = useMemo(
    () => canAccessJournal && canViewCurrentEmployee,
    [canAccessJournal, canViewCurrentEmployee]
  );

  const canCreateRecord = useMemo(
    () => canViewPage && RecordsIsCreate,
    [RecordsIsCreate, canViewPage]
  );

  const canEditRecord = useMemo(
    () => canViewPage && RecordsIsEdit,
    [RecordsIsEdit, canViewPage]
  );

  const hasWorkScheduleBaseAccess = useMemo(
    () =>
      WorkSchedulesIsOn &&
      WorkSchedulesIsCreate &&
      WorkSchedulesIsEdit &&
      RecordsIsOn &&
      RecordsIsJournal,
    [
      RecordsIsJournal,
      RecordsIsOn,
      WorkSchedulesIsCreate,
      WorkSchedulesIsEdit,
      WorkSchedulesIsOn,
    ]
  );

  const getCanViewDate = useCallback(
    (date: string | Moment) => {
      const dateMoment = moment.isMoment(date) ? date : moment(date);

      return IsViewPastRecords || !dateMoment.isBefore(todayMoment, 'day');
    },
    [IsViewPastRecords, todayMoment]
  );

  const getCanCreateRecordOnDate = useCallback(
    (date: string | Moment) => canCreateRecord && getCanViewDate(date),
    [canCreateRecord, getCanViewDate]
  );

  const getCanOpenRecordOnDate = useCallback(
    (date: string | Moment) => canViewPage && getCanViewDate(date),
    [canViewPage, getCanViewDate]
  );

  const getCanManageRecordOnDate = useCallback(
    (date: string | Moment) => canEditRecord && getCanViewDate(date),
    [canEditRecord, getCanViewDate]
  );

  const getCanAddWorkScheduleOnDate = useCallback(
    (date: string | Moment) => {
      if (!hasWorkScheduleBaseAccess) {
        return false;
      }

      const dateMoment = moment.isMoment(date) ? date : moment(date);

      if (dateMoment.isBefore(todayMoment, 'day')) {
        return WorkSchedulesIsEditPast;
      }

      return true;
    },
    [WorkSchedulesIsEditPast, hasWorkScheduleBaseAccess, todayMoment]
  );

  return {
    canAccessJournal,
    canCreateRecord,
    canEditRecord,
    canShowContacts: IsShowContacts,
    canViewCurrentEmployee,
    canViewPage,
    canViewPastRecords: IsViewPastRecords,
    getCanAddWorkScheduleOnDate,
    getCanCreateRecordOnDate,
    getCanOpenRecordOnDate,
    getCanManageRecordOnDate,
    getCanViewDate,
  };
};
